import { useEffect, useMemo, useState } from "react";
import { TextInput } from "@normasteaching/norma-ui";
import { Layers, Package, X } from "lucide-react";
import { NormasTeachingProductDTO } from "../../ProductService/NormasTeachingProduct/useNormasTeachingProductById.ts";
import { NormasTeachingBundleDTO } from "../../ProductService/NormasTeachingBundle/useNormasTeachingBundleById.ts";
import { matchesItemSearch } from "../../UI/Utils/matchesItemSearch.ts";
import { OverlayDialog } from "../../UI/Modal/OverlayDialog.tsx";

export type PurchasableItemType = "product" | "bundle";

export type PurchasableItemRef = {
  type: PurchasableItemType;
  id: string;
};

type PickerRow = {
  id: string;
  name: string;
  variantName: string;
  priceInCents: number;
};

function PickerRowButton({
  row,
  isSelected,
  icon,
  onPick,
}: {
  row: PickerRow;
  isSelected: boolean;
  icon: React.ReactNode;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      className={`w-full text-left p-3 border rounded-lg transition flex items-center gap-3 ${
        isSelected
          ? "border-blue-500 bg-blue-50"
          : "border-gray-200 hover:bg-gray-50"
      }`}
    >
      <div className="flex-1 min-w-0">
        <p className="font-medium">{row.name}</p>
        <p className="text-sm text-gray-500">
          {row.variantName} - €{(row.priceInCents / 100).toFixed(2)}
        </p>
        <p className="text-xs text-gray-400 font-mono truncate">{row.id}</p>
      </div>
      {icon}
    </button>
  );
}

export function PurchasableItemPickerModal({
  isOpen,
  onClose,
  products,
  bundles,
  selected,
  onPick,
  title = "Seleziona prodotto o bundle",
}: {
  isOpen: boolean;
  onClose: () => void;
  products: NormasTeachingProductDTO[];
  bundles: NormasTeachingBundleDTO[];
  selected?: PurchasableItemRef;
  onPick: (item: PurchasableItemRef) => void;
  title?: string;
}) {
  const [activeTab, setActiveTab] = useState<PurchasableItemType>("product");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setSearch("");
    setActiveTab(selected?.type ?? "product");
  }, [isOpen, selected?.type]);

  const productRows: PickerRow[] = useMemo(
    () =>
      products
        .filter((p) =>
          matchesItemSearch(search, [
            p._id,
            p.productName,
            p.variantProductName,
          ]),
        )
        .map((p) => ({
          id: p._id,
          name: p.productName,
          variantName: p.variantProductName,
          priceInCents: p.priceInCents.amount,
        })),
    [products, search],
  );

  const bundleRows: PickerRow[] = useMemo(
    () =>
      bundles
        .filter((b) =>
          matchesItemSearch(search, [b._id, b.bundleName, b.variantBundleName]),
        )
        .map((b) => ({
          id: b._id,
          name: b.bundleName,
          variantName: b.variantBundleName,
          priceInCents: b.priceInCents.amount,
        })),
    [bundles, search],
  );

  if (!isOpen) return null;

  const rows = activeTab === "product" ? productRows : bundleRows;
  const pick = (id: string) => {
    onPick({ type: activeTab, id });
    onClose();
  };

  const tabClassName = (tab: PurchasableItemType) =>
    `flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
      activeTab === tab
        ? "border-b-2 border-blue-600 text-blue-600"
        : "text-gray-500 hover:text-gray-700"
    }`;

  return (
    <OverlayDialog
      onClose={onClose}
      panelClassName="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col"
    >
      <div className="flex items-center justify-between p-6 border-b">
        <h3 className="text-xl font-bold">{title}</h3>
        <button
          type="button"
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-lg transition"
        >
          <X size={20} />
        </button>
      </div>

      <div className={bundles.length > 0 ? "flex border-b" : "hidden"}>
        <button
          type="button"
          onClick={() => setActiveTab("product")}
          className={tabClassName("product")}
        >
          <Package size={18} />
          Prodotti ({productRows.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("bundle")}
          className={tabClassName("bundle")}
        >
          <Layers size={18} />
          Bundle ({bundleRows.length})
        </button>
      </div>

      <div className="px-6 pt-4">
        <TextInput
          placeholder="Cerca per nome, variante o ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-2">
        {rows.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            {search.trim()
              ? "Nessun elemento corrisponde alla ricerca"
              : "Nessun elemento disponibile"}
          </p>
        ) : (
          rows.map((row) => (
            <PickerRowButton
              key={row.id}
              row={row}
              isSelected={
                selected?.type === activeTab && selected.id === row.id
              }
              icon={
                activeTab === "product" ? (
                  <Package size={18} className="text-gray-400" />
                ) : (
                  <Layers size={18} className="text-gray-400" />
                )
              }
              onPick={() => pick(row.id)}
            />
          ))
        )}
      </div>
    </OverlayDialog>
  );
}
